import React, { useState } from 'react';
import { ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Bar } from 'recharts';
import { CalculationResult } from '../../types';
import { colorSystem } from '../../utils/colorSystem';
import { ChartToggle } from './ChartToggle';
import { formatAxisValue, formatTooltipValue } from '../../utils/formatters';

interface EPTTrendChartProps {
  results: CalculationResult[];
}

// Utility function to truncate Y-axis labels
const truncateLabel = (label: string, maxLength: number = 16): string => {
  if (label.length <= maxLength) return label;
  return label.substring(0, maxLength - 3) + '...';
};

export function EPTTrendChart({ results }: EPTTrendChartProps) {
  const [showSecondaryAxis, setShowSecondaryAxis] = useState(true);

  const chartData = React.useMemo(() => {
    colorSystem.reset();
    
    const dataMap = new Map<string, { [key: string]: any }>();
    
    results.forEach(result => {
      const date = result.calculationDate;
      if (!dataMap.has(date)) {
        dataMap.set(date, {
          date,
          productivity: result.productivity,
          values: {}
        });
      }
      const roleKey = `${result.role}-${result.experience}`;
      dataMap.get(date)!.values[roleKey] = result.ept;
    });

    const data = Array.from(dataMap.entries())
      .map(([date, values]) => ({
        date,
        ...values
      }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    const roleKeys = Array.from(new Set(
      results.map(r => `${r.role}-${r.experience}`)
    ));

    return { data, roleKeys };
  }, [results]);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload || !payload.length) return null;

    return (
      <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
        <p className="font-medium text-gray-900 mb-2">
          {new Date(label).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          })}
        </p>
        {payload.map((entry: any) => {
          if (entry.dataKey === 'productivity') {
            return (
              <div key={entry.dataKey} className="flex items-center gap-2 text-sm">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: entry.color }}
                />
                <span className="text-gray-700">Expected Productivity:</span>
                <span className="font-medium">{formatTooltipValue(entry.value, '', '%')}</span>
              </div>
            );
          }

          return (
            <div key={entry.dataKey} className="flex items-center gap-2 text-sm">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-gray-700">{entry.name}:</span>
              <span className="font-medium">{formatTooltipValue(entry.value, '', 's')}</span>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="w-full h-[600px] bg-white p-6 rounded-xl shadow-sm chart-container">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-xl font-semibold text-gray-900">EPT Trend Analysis</h3>
          <p className="text-sm text-gray-500">
            Change in load speeds over time with Signature Success
          </p>
        </div>
        <ChartToggle
          showSecondaryAxis={showSecondaryAxis}
          onToggle={setShowSecondaryAxis}
          label="Show Productivity"
        />
      </div>
      
      <ResponsiveContainer width="100%" height="87%">
        <ComposedChart 
          data={chartData.data}
         margin={{ top: 60, right: 30, left: 20, bottom: 60 }}
        >
          <defs>
            <linearGradient id="productivityGradient" x1="0" y1="1" x2="0" y2="0">
              <stop offset="0%" stopColor="#FCD34D" stopOpacity={0.3}/>
              <stop offset="100%" stopColor="#F59E0B" stopOpacity={0.8}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="date"
            label={{ 
              value: 'Analysis Date', 
             position: 'bottom', 
             offset: 40,
             style: { fontWeight: 'bold' }
            }}
            tick={{ dy: 10 }}
          />
          <YAxis
            yAxisId="left"
           width={100}
            label={{ 
              value: truncateLabel('EPT Score (seconds)'), 
             angle: 0,
             position: 'top',
            dy: -30,
            textAnchor: 'start',
            dx: 50,
             style: { fontWeight: 'bold' }
            }}
            tickFormatter={formatAxisValue}
          />
          {showSecondaryAxis && (
            <YAxis
              yAxisId="right"
              orientation="right"
             width={100}
              domain={[0, 100]}
              label={{
                value: truncateLabel('Expected Productivity (%)'),
               angle: 0,
               position: 'top',
              dy: -30,
              textAnchor: 'end',
              dx: -50,
               style: { fontWeight: 'bold' }
              }}
              tickFormatter={formatAxisValue}
            />
          )}
          <Tooltip content={<CustomTooltip />} />
          <Legend 
            verticalAlign="bottom" 
            height={36}
            wrapperStyle={{
              paddingTop: '20px',
              marginBottom: '-45px'
            }}
          />
          {chartData.roleKeys.map(roleKey => {
            const [role, experience] = roleKey.split('-');
            return (
              <Line
                key={roleKey}
                yAxisId="left"
                type="monotone"
                dataKey={`values.${roleKey}`}
                name={roleKey}
                stroke={colorSystem.getColor(role, experience)}
                strokeWidth={2}
                dot={{ r: 4 }}
                activeDot={{ r: 6 }}
                connectNulls={true}
              />
            );
          })}
          {showSecondaryAxis && (
            <Bar
              yAxisId="right"
              dataKey="productivity"
              name="Expected Productivity"
              fill="url(#productivityGradient)"
              opacity={0.6}
              barSize={20}
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}